/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { getPhotoFilterCss, type PhotoFilter } from './image-editor-types'

const GAUSSIAN_BOX_PASSES = 3
const FILTER_FUNCTION_PATTERN = /([a-z-]+)\(([^)]+)\)/g

type ColorMatrix = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
]

type FilterStep =
  | { name: 'grayscale' | 'sepia' | 'saturate' | 'brightness' | 'contrast'; amount: number }
  | { name: 'hue-rotate'; degrees: number }

const IDENTITY_MATRIX: ColorMatrix = [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0]

let canvasFilterSupport: boolean | undefined

/**
 * Safari ships CanvasRenderingContext2D.filter behind a default-off flag, including
 * current iOS and macOS releases. Setting `grayscale()` or `blur()` succeeds and then
 * drawImage ignores it. Probe with a real draw so a future Safari that enables the
 * flag can use the native path.
 */
export const supportsCanvasFilters = () => {
  if (canvasFilterSupport !== undefined) {
    return canvasFilterSupport
  }
  if (typeof document === 'undefined') {
    canvasFilterSupport = false
    return false
  }

  const source = document.createElement('canvas')
  source.width = 1
  source.height = 1
  const sourceContext = source.getContext('2d')
  const output = document.createElement('canvas')
  output.width = 1
  output.height = 1
  const outputContext = output.getContext('2d', { willReadFrequently: true })
  if (!(sourceContext && outputContext && 'filter' in outputContext)) {
    canvasFilterSupport = false
    return false
  }

  sourceContext.fillStyle = '#ff0000'
  sourceContext.fillRect(0, 0, 1, 1)

  try {
    outputContext.filter = 'grayscale(1)'
    outputContext.drawImage(source, 0, 0)
    outputContext.filter = 'none'
    const pixel = outputContext.getImageData(0, 0, 1, 1).data
    canvasFilterSupport = pixel[0] < 250 && pixel[1] > 5 && pixel[2] > 5
  } catch {
    canvasFilterSupport = false
  }

  return canvasFilterSupport
}

const clampByte = (value: number) => Math.max(0, Math.min(255, Math.round(value)))

const parseAmount = (raw: string) => {
  const value = Number.parseFloat(raw)
  return raw.trim().endsWith('%') ? value / 100 : value
}

const parseFilterSteps = (filterCss: string): FilterStep[] => {
  if (filterCss === 'none') {
    return []
  }

  const steps: FilterStep[] = []
  for (const match of filterCss.matchAll(FILTER_FUNCTION_PATTERN)) {
    const name = match[1]
    const argument = match[2] ?? ''
    if (name === 'hue-rotate') {
      steps.push({ name, degrees: Number.parseFloat(argument) })
      continue
    }
    if (
      name === 'grayscale' ||
      name === 'sepia' ||
      name === 'saturate' ||
      name === 'brightness' ||
      name === 'contrast'
    ) {
      steps.push({ name, amount: parseAmount(argument) })
      continue
    }
    throw new Error(`Unsupported photo filter function: ${name}`)
  }
  return steps
}

const multiplyMatrix = (left: ColorMatrix, right: ColorMatrix): ColorMatrix => {
  const out: ColorMatrix = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 5; col += 1) {
      let sum = col === 4 ? left[row * 5 + 4] : 0
      for (let channel = 0; channel < 4; channel += 1) {
        sum += left[row * 5 + channel] * right[channel * 5 + col]
      }
      out[row * 5 + col] = sum
    }
  }
  return out
}

const grayscaleMatrix = (amount: number): ColorMatrix => {
  const rest = 1 - amount
  return [
    0.2126 + 0.7874 * rest,
    0.7152 - 0.7152 * rest,
    0.0722 - 0.0722 * rest,
    0,
    0,
    0.2126 - 0.2126 * rest,
    0.7152 + 0.2848 * rest,
    0.0722 - 0.0722 * rest,
    0,
    0,
    0.2126 - 0.2126 * rest,
    0.7152 - 0.7152 * rest,
    0.0722 + 0.9278 * rest,
    0,
    0,
    0,
    0,
    0,
    1,
    0,
  ]
}

const sepiaMatrix = (amount: number): ColorMatrix => {
  const rest = 1 - amount
  return [
    0.393 + 0.607 * rest,
    0.769 - 0.769 * rest,
    0.189 - 0.189 * rest,
    0,
    0,
    0.349 - 0.349 * rest,
    0.686 + 0.314 * rest,
    0.168 - 0.168 * rest,
    0,
    0,
    0.272 - 0.272 * rest,
    0.534 - 0.534 * rest,
    0.131 + 0.869 * rest,
    0,
    0,
    0,
    0,
    0,
    1,
    0,
  ]
}

const saturateMatrix = (amount: number): ColorMatrix => [
  0.213 + 0.787 * amount,
  0.715 - 0.715 * amount,
  0.072 - 0.072 * amount,
  0,
  0,
  0.213 - 0.213 * amount,
  0.715 + 0.285 * amount,
  0.072 - 0.072 * amount,
  0,
  0,
  0.213 - 0.213 * amount,
  0.715 - 0.715 * amount,
  0.072 + 0.928 * amount,
  0,
  0,
  0,
  0,
  0,
  1,
  0,
]

const hueRotateMatrix = (degrees: number): ColorMatrix => {
  const radians = (degrees * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  return [
    0.213 + cos * 0.787 - sin * 0.213,
    0.715 - cos * 0.715 - sin * 0.715,
    0.072 - cos * 0.072 + sin * 0.928,
    0,
    0,
    0.213 - cos * 0.213 + sin * 0.143,
    0.715 + cos * 0.285 + sin * 0.14,
    0.072 - cos * 0.072 - sin * 0.283,
    0,
    0,
    0.213 - cos * 0.213 - sin * 0.787,
    0.715 - cos * 0.715 + sin * 0.715,
    0.072 + cos * 0.928 + sin * 0.072,
    0,
    0,
    0,
    0,
    0,
    1,
    0,
  ]
}

const brightnessMatrix = (amount: number): ColorMatrix => [
  amount,
  0,
  0,
  0,
  0,
  0,
  amount,
  0,
  0,
  0,
  0,
  0,
  amount,
  0,
  0,
  0,
  0,
  0,
  1,
  0,
]

const contrastMatrix = (amount: number): ColorMatrix => {
  const intercept = 0.5 * (1 - amount)
  return [amount, 0, 0, 0, intercept, 0, amount, 0, 0, intercept, 0, 0, amount, 0, intercept, 0, 0, 0, 1, 0]
}

const matrixForStep = (step: FilterStep) => {
  if (step.name === 'grayscale') {
    return grayscaleMatrix(step.amount)
  }
  if (step.name === 'sepia') {
    return sepiaMatrix(step.amount)
  }
  if (step.name === 'saturate') {
    return saturateMatrix(step.amount)
  }
  if (step.name === 'hue-rotate') {
    return hueRotateMatrix(step.degrees)
  }
  if (step.name === 'brightness') {
    return brightnessMatrix(step.amount)
  }
  return contrastMatrix(step.amount)
}

const matrixForFilterCss = (filterCss: string) => {
  let matrix = IDENTITY_MATRIX
  for (const step of parseFilterSteps(filterCss)) {
    matrix = multiplyMatrix(matrixForStep(step), matrix)
  }
  return matrix
}

export const applyColorMatrix = (pixels: Uint8ClampedArray, matrix: ColorMatrix) => {
  for (let index = 0; index < pixels.length; index += 4) {
    const red = pixels[index] / 255
    const green = pixels[index + 1] / 255
    const blue = pixels[index + 2] / 255
    const alpha = pixels[index + 3] / 255
    pixels[index] = clampByte(
      (matrix[0] * red + matrix[1] * green + matrix[2] * blue + matrix[3] * alpha + matrix[4]) * 255
    )
    pixels[index + 1] = clampByte(
      (matrix[5] * red + matrix[6] * green + matrix[7] * blue + matrix[8] * alpha + matrix[9]) * 255
    )
    pixels[index + 2] = clampByte(
      (matrix[10] * red + matrix[11] * green + matrix[12] * blue + matrix[13] * alpha + matrix[14]) * 255
    )
    pixels[index + 3] = clampByte(
      (matrix[15] * red + matrix[16] * green + matrix[17] * blue + matrix[18] * alpha + matrix[19]) * 255
    )
  }
}

export const applyPhotoFilterPixels = (pixels: Uint8ClampedArray, photoFilter: PhotoFilter) => {
  const filterCss = getPhotoFilterCss(photoFilter)
  if (filterCss === 'none') {
    return
  }
  applyColorMatrix(pixels, matrixForFilterCss(filterCss))
}

export const applyPhotoFilter = (context: CanvasRenderingContext2D, photoFilter: PhotoFilter) => {
  if (photoFilter === 'original') {
    return
  }
  const imageData = context.getImageData(0, 0, context.canvas.width, context.canvas.height)
  applyPhotoFilterPixels(imageData.data, photoFilter)
  context.putImageData(imageData, 0, 0)
}

const boxesForGaussian = (sigma: number, passCount: number) => {
  const idealWidth = Math.sqrt((12 * sigma * sigma) / passCount + 1)
  let lowerWidth = Math.floor(idealWidth)
  if (lowerWidth % 2 === 0) {
    lowerWidth -= 1
  }
  const upperWidth = lowerWidth + 2
  const idealPasses =
    (12 * sigma * sigma - passCount * lowerWidth * lowerWidth - 4 * passCount * lowerWidth - 3 * passCount) /
    (-4 * lowerWidth - 4)
  const lowerPasses = Math.round(idealPasses)
  const widths: number[] = []
  for (let index = 0; index < passCount; index += 1) {
    widths.push(index < lowerPasses ? lowerWidth : upperWidth)
  }
  return widths
}

const blurHorizontal = (
  source: Uint8ClampedArray,
  target: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number
) => {
  const windowSize = radius * 2 + 1
  for (let y = 0; y < height; y += 1) {
    let red = 0
    let green = 0
    let blue = 0
    let alpha = 0
    const row = y * width
    for (let offset = -radius; offset <= radius; offset += 1) {
      if (offset < 0 || offset >= width) {
        continue
      }
      const index = (row + offset) * 4
      red += source[index]
      green += source[index + 1]
      blue += source[index + 2]
      alpha += source[index + 3]
    }

    for (let x = 0; x < width; x += 1) {
      const index = (row + x) * 4
      target[index] = Math.round(red / windowSize)
      target[index + 1] = Math.round(green / windowSize)
      target[index + 2] = Math.round(blue / windowSize)
      target[index + 3] = Math.round(alpha / windowSize)

      const removeX = x - radius
      const addX = x + radius + 1
      if (removeX >= 0 && removeX < width) {
        const removeIndex = (row + removeX) * 4
        red -= source[removeIndex]
        green -= source[removeIndex + 1]
        blue -= source[removeIndex + 2]
        alpha -= source[removeIndex + 3]
      }
      if (addX >= 0 && addX < width) {
        const addIndex = (row + addX) * 4
        red += source[addIndex]
        green += source[addIndex + 1]
        blue += source[addIndex + 2]
        alpha += source[addIndex + 3]
      }
    }
  }
}

const blurVertical = (
  source: Uint8ClampedArray,
  target: Uint8ClampedArray,
  width: number,
  height: number,
  radius: number
) => {
  const windowSize = radius * 2 + 1
  for (let x = 0; x < width; x += 1) {
    let red = 0
    let green = 0
    let blue = 0
    let alpha = 0
    for (let offset = -radius; offset <= radius; offset += 1) {
      if (offset < 0 || offset >= height) {
        continue
      }
      const index = (offset * width + x) * 4
      red += source[index]
      green += source[index + 1]
      blue += source[index + 2]
      alpha += source[index + 3]
    }

    for (let y = 0; y < height; y += 1) {
      const index = (y * width + x) * 4
      target[index] = Math.round(red / windowSize)
      target[index + 1] = Math.round(green / windowSize)
      target[index + 2] = Math.round(blue / windowSize)
      target[index + 3] = Math.round(alpha / windowSize)

      const removeY = y - radius
      const addY = y + radius + 1
      if (removeY >= 0 && removeY < height) {
        const removeIndex = (removeY * width + x) * 4
        red -= source[removeIndex]
        green -= source[removeIndex + 1]
        blue -= source[removeIndex + 2]
        alpha -= source[removeIndex + 3]
      }
      if (addY >= 0 && addY < height) {
        const addIndex = (addY * width + x) * 4
        red += source[addIndex]
        green += source[addIndex + 1]
        blue += source[addIndex + 2]
        alpha += source[addIndex + 3]
      }
    }
  }
}

const premultiply = (pixels: Uint8ClampedArray) => {
  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3] / 255
    if (alpha === 1) {
      continue
    }
    pixels[index] = Math.round(pixels[index] * alpha)
    pixels[index + 1] = Math.round(pixels[index + 1] * alpha)
    pixels[index + 2] = Math.round(pixels[index + 2] * alpha)
  }
}

const unpremultiply = (pixels: Uint8ClampedArray) => {
  for (let index = 0; index < pixels.length; index += 4) {
    const alpha = pixels[index + 3]
    if (alpha === 255) {
      continue
    }
    if (alpha === 0) {
      pixels[index] = 0
      pixels[index + 1] = 0
      pixels[index + 2] = 0
      continue
    }
    const scale = 255 / alpha
    pixels[index] = clampByte(pixels[index] * scale)
    pixels[index + 1] = clampByte(pixels[index + 1] * scale)
    pixels[index + 2] = clampByte(pixels[index + 2] * scale)
  }
}

/** CSS `blur()` is a Gaussian whose standard deviation is the pixel radius. */
export const gaussianBlurPixels = (pixels: Uint8ClampedArray, width: number, height: number, sigma: number) => {
  if (sigma < 0.5 || width === 0 || height === 0) {
    return
  }

  premultiply(pixels)
  const scratch = new Uint8ClampedArray(pixels.length)
  for (const boxWidth of boxesForGaussian(sigma, GAUSSIAN_BOX_PASSES)) {
    const radius = (boxWidth - 1) / 2
    if (radius < 1) {
      continue
    }
    blurHorizontal(pixels, scratch, width, height, radius)
    blurVertical(scratch, pixels, width, height, radius)
  }
  unpremultiply(pixels)
}

export const blurCanvas = (canvas: HTMLCanvasElement, radiusPx: number) => {
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) {
    return
  }
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
  gaussianBlurPixels(imageData.data, canvas.width, canvas.height, radiusPx)
  context.putImageData(imageData, 0, 0)
}
